import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart, Bar, Area, AreaChart,
  PieChart, Pie, Cell, Line, LineChart,
} from "recharts";
import "./Analytics.css";

const IconMessages = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconDoc = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const IconSmile = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);
const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const IconTag = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={20} height={20}>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const MiniSparkline = ({ color, data, dataKey }) => (
  <ResponsiveContainer width="100%" height={48}>
    <LineChart data={data}>
      <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
    </LineChart>
  </ResponsiveContainer>
);

const KpiCard = ({ title, value, sub, subGreen, icon, iconBg, sparkColor, sparkData, sparkKey }) => (
  <div className="kpi-card">
    <div className="kpi-top">
      <div className="kpi-text">
        <span className="kpi-title">{title}</span>
        <span className="kpi-value">{value}</span>
        <span className={`kpi-sub${subGreen ? " kpi-sub--green" : ""}`}>{sub}</span>
      </div>
      <div className="kpi-icon" style={{ background: iconBg }}>{icon}</div>
    </div>
    <div className="kpi-spark">
      <MiniSparkline color={sparkColor} data={sparkData} dataKey={sparkKey} />
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="tooltip-label">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="tooltip-row">
          <span className="tooltip-dot" style={{ background: p.color }} />
          <span>{p.name}: <strong>{p.value}</strong></span>
        </div>
      ))}
    </div>
  );
};

const SectionHeader = ({ title, icon }) => (
  <div className="section-header">
    <span className="section-icon">{icon}</span>
    <span className="section-title">{title}</span>
  </div>
);

const DaysSelect = ({ value, onChange }) => (
  <div className="chart-days-select">
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="days-select"
    >
      <option value={7}>Last 7 days</option>
      <option value={14}>Last 14 days</option>
      <option value={30}>Last 30 days</option>
    </select>
    <IconChevronDown />
  </div>
);

const SENTIMENT_CONFIG = {
  positive: { label: "Positive", color: "#22c55e", bg: "#f0fdf4", bar: "#22c55e" },
  neutral: { label: "Neutral", color: "#f59e0b", bg: "#fffbeb", bar: "#f59e0b" },
  negative: { label: "Negative", color: "#ef4444", bg: "#fef2f2", bar: "#ef4444" },
};

const SentimentBar = ({ items }) => {
  const total = items.reduce((a, i) => a + i.count, 0) || 1;
  return (
    <div className="sentiment-section">
      <div className="sentiment-stacked-bar">
        {items.map((item) => {
          const cfg = SENTIMENT_CONFIG[item.sentiment] || { bar: "#94a3b8", label: item.sentiment };
          const pct = ((item.count / total) * 100).toFixed(1);
          return (
            <div
              key={item.sentiment}
              className="sentiment-segment"
              style={{ width: `${pct}%`, background: cfg.bar }}
              title={`${cfg.label}: ${pct}%`}
            />
          );
        })}
      </div>
      <div className="sentiment-legend">
        {items.map((item) => {
          const cfg = SENTIMENT_CONFIG[item.sentiment] || {
            color: "#94a3b8", bg: "#f1f5f9", label: item.sentiment,
          };
          const pct = ((item.count / total) * 100).toFixed(1);
          return (
            <div key={item.sentiment} className="sentiment-legend-item">
              <div className="sentiment-legend-pill" style={{ background: cfg.bg, borderColor: cfg.color }}>
                <span className="sentiment-dot" style={{ background: cfg.color }} />
                <span className="sentiment-label" style={{ color: cfg.color }}>{cfg.label}</span>
              </div>
              <span className="sentiment-count">
                {item.count} <span className="sentiment-pct">({pct}%)</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RESOLUTION_COLORS = {
  confirmed: "#3b82f6",
  assumed: "#8b5cf6",
  unresolved: "#ef4444",
  escalated: "#f59e0b",
};
const RESOLUTION_DEFAULT_COLOR = "#94a3b8";

const ResolutionDonut = ({ items }) => {
  const total = items.reduce((a, i) => a + i.count, 0) || 1;
  const data = items.map((i) => ({ name: i._id || "Unknown", value: i.count }));
  return (
    <div className="resolution-wrap">
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            paddingAngle={3}
            dataKey="value"
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={RESOLUTION_COLORS[entry.name] || RESOLUTION_DEFAULT_COLOR} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [`${value} (${((value / total) * 100).toFixed(1)}%)`, ""]}
            contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="resolution-legend">
        {data.map((entry) => {
          const color = RESOLUTION_COLORS[entry.name] || RESOLUTION_DEFAULT_COLOR;
          const pct = ((entry.value / total) * 100).toFixed(1);
          return (
            <div key={entry.name} className="resolution-legend-item">
              <span className="resolution-dot" style={{ background: color }} />
              <span className="resolution-name">{entry.name}</span>
              <span className="resolution-val">{entry.value}</span>
              <span className="resolution-pct">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UnansweredTable = ({ items }) => (
  <div className="table-wrap">
    {items.length === 0 ? (
      <div className="empty-state">No unanswered questions — great job!</div>
    ) : (
      <table className="analytics-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Question</th>
            <th className="col-right">Count</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i}>
              <td className="row-num">{i + 1}</td>
              <td className="question-cell">{row._id || "—"}</td>
              <td className="col-right"><span className="count-badge">{row.count}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const LocationsTable = ({ items }) => {
  const total = items.reduce((a, i) => a + i.count, 0) || 1;
  return (
    <div className="table-wrap">
      {items.length === 0 ? (
        <div className="empty-state">No location data available.</div>
      ) : (
        <table className="analytics-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Country</th>
              <th>Share</th>
              <th className="col-right">Messages</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, i) => {
              const pct = ((row.count / total) * 100).toFixed(1);
              return (
                <tr key={i}>
                  <td className="row-num">{i + 1}</td>
                  <td>{row._id || "Unknown"}</td>
                  <td>
                    <div className="loc-bar-wrap">
                      <div className="loc-bar" style={{ width: `${pct}%` }} />
                      <span className="loc-pct">{pct}%</span>
                    </div>
                  </td>
                  <td className="col-right"><span className="count-badge">{row.count}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const MOCK_RESOLUTION = [
  { _id: "confirmed", count: 142 },
  { _id: "assumed", count: 58 },
  { _id: "unresolved", count: 34 },
];
const MOCK_UNANSWERED = [
  { _id: "How do I reset my password?", count: 18 },
  { _id: "What are your pricing plans?", count: 14 },
  { _id: "Can I export my data?", count: 11 },
  { _id: "Do you support SSO?", count: 9 },
  { _id: "Where is my invoice?", count: 7 },
];
const MOCK_TOPICS = [
  { _id: "Information", count: 92 },
  { _id: "How To", count: 78 },
  { _id: "Technical Support", count: 61 },
  { _id: "General", count: 44 },
  { _id: "Billing & Pricing", count: 38 },
  { _id: "Account & Access", count: 27 },
];
const MOCK_LOCATIONS = [
  { _id: "United States", count: 134 },
  { _id: "India", count: 89 },
  { _id: "United Kingdom", count: 54 },
  { _id: "Germany", count: 31 },
  { _id: "Canada", count: 22 },
];
const MOCK_SENTIMENT = [
  { sentiment: "positive", count: 198 },
  { sentiment: "neutral", count: 87 },
  { sentiment: "negative", count: 41 },
];

function buildMockUsage() {
  const base = new Date("2025-04-16");
  return Array.from({ length: 14 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return {
      date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      messages: Math.floor(Math.random() * 80 + 20),
      sessions: Math.floor(Math.random() * 40 + 10),
    };
  });
}

export default function Analytics({ apiBase, email, botName, days = 14 }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [usage, setUsage] = useState([]);
  const [resolution, setResolution] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [topics, setTopics] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sentiment, setSentiment] = useState([]);
  const [selectedDays, setSelectedDays] = useState(days);
  const [refreshKey, setRefreshKey] = useState(0);
  const [usingMock, setUsingMock] = useState(false);

  const mockUsage = useRef(buildMockUsage()).current;

  const applyMockData = useCallback(() => {
    setUsage(mockUsage);
    setResolution(MOCK_RESOLUTION);
    setUnanswered(MOCK_UNANSWERED);
    setTopics(MOCK_TOPICS);
    setLocations(MOCK_LOCATIONS);
    setSentiment(MOCK_SENTIMENT);
    setUsingMock(true);
    setLoading(false);
  }, [mockUsage]);

  useEffect(() => {
    if (!apiBase || !email || !botName) {
      applyMockData();
      return;
    }

    let cancelled = false;
    const enc = (v) => encodeURIComponent(v);
    const base = `${apiBase}/api/analytics`;

    async function run() {
      setLoading(true);
      try {
        const [s, u, r, un, t, loc, sent] = await Promise.all([
          fetch(`${base}/summary/${enc(email)}/${enc(botName)}`).then((res) => res.json()),
          fetch(`${base}/usage/${enc(email)}/${enc(botName)}?days=${selectedDays}`).then((res) => res.json()),
          fetch(`${base}/resolution/${enc(email)}/${enc(botName)}`).then((res) => res.json()),
          fetch(`${base}/unanswered/${enc(email)}/${enc(botName)}`).then((res) => res.json()),
          fetch(`${base}/topics/${enc(email)}/${enc(botName)}`).then((res) => res.json()),
          fetch(`${base}/locations/${enc(email)}/${enc(botName)}`).then((res) => res.json()),
          fetch(`${base}/sentiment/${enc(email)}/${enc(botName)}`).then((res) => res.json()),
        ]);
        if (!cancelled) {
          setSummary(s);
          setUsage(u.items || []);
          setResolution(r.items || []);
          setUnanswered(un.items || []);
          setTopics(t.items || []);
          setLocations(loc.items || []);
          setSentiment(sent.items || []);
          setUsingMock(false);
        }
      } catch {
        if (!cancelled) applyMockData();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [apiBase, email, botName, selectedDays, refreshKey, applyMockData]);

  const displayData = usage.length ? usage : mockUsage;
  const topicsChartData = useMemo(
    () => topics.map((t) => ({ name: t._id || "Unknown", count: t.count })),
    [topics],
  );

  return (
    <div className="analytics-root">
      {usingMock && (
        <div className="mock-warning">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span>
            <strong>API unavailable</strong> — Sample data is shown below for preview purposes.
            Real data will appear once the API is connected.
          </span>
        </div>
      )}
      <div className="analytics-header">
        <div>
          <h1 className="analytics-title">Analytics</h1>
          <p className="analytics-subtitle">Overview of your chatbot performance</p>
        </div>
        <div className="analytics-header-actions">
          <button
            type="button"
            className="btn-refresh"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <KpiCard
          title="Total Messages"
          value={summary?.total_messages ?? "—"}
          sub={`Last ${selectedDays} days`}
          icon={<IconMessages />}
          iconBg="#eff6ff"
          sparkColor="#3b82f6"
          sparkData={displayData}
          sparkKey="messages"
        />
        <KpiCard
          title="Unique Users"
          value={summary?.unique_users ?? "—"}
          sub={`Last ${selectedDays} days`}
          icon={<IconUsers />}
          iconBg="#f5f3ff"
          sparkColor="#8b5cf6"
          sparkData={displayData}
          sparkKey="sessions"
        />
        <KpiCard
          title="Avg Latency (ms)"
          value={summary?.avg_latency_ms != null ? `${summary.avg_latency_ms}ms` : "—"}
          sub="Optimal healthy"
          subGreen
          icon={<IconClock />}
          iconBg="#f0fdf4"
          sparkColor="#22c55e"
          sparkData={displayData}
          sparkKey="messages"
        />
        <KpiCard
          title="Avg Answer Length"
          value={summary?.avg_answer_chars != null ? `${summary.avg_answer_chars}` : "—"}
          sub="Characters"
          icon={<IconDoc />}
          iconBg="#fff7ed"
          sparkColor="#f97316"
          sparkData={displayData}
          sparkKey="sessions"
        />
      </div>

      <div className="chart-card">
        <div className="chart-card-header">
          <SectionHeader title="Daily Usage" icon={<IconMessages />} />
          <DaysSelect value={selectedDays} onChange={setSelectedDays} />
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={displayData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="gradMessages" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.18} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.12} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8, color: "#64748b" }} />
            <Area
              type="monotone" dataKey="messages" yAxisId="left"
              stroke="#3b82f6" strokeWidth={2.5} fill="url(#gradMessages)"
              dot={{ r: 4, fill: "#3b82f6", strokeWidth: 0 }} activeDot={{ r: 6 }}
              name="Messages"
            />
            <Area
              type="monotone" dataKey="sessions" yAxisId="right"
              stroke="#8b5cf6" strokeWidth={2.5} fill="url(#gradSessions)"
              dot={{ r: 4, fill: "#8b5cf6", strokeWidth: 0 }} activeDot={{ r: 6 }}
              name="Sessions"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card">
        <div className="chart-card-header">
          <SectionHeader title="Messages Per Day" icon={<IconDoc />} />
          <DaysSelect value={selectedDays} onChange={setSelectedDays} />
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={displayData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }} barSize={28}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="messages" name="Messages" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="two-col-grid">
        <div className="chart-card">
          <div className="chart-card-header">
            <SectionHeader title="Resolution Breakdown" icon={<IconAlert />} />
          </div>
          <ResolutionDonut items={resolution} />
        </div>
        <div className="chart-card">
          <div className="chart-card-header">
            <SectionHeader title="User Sentiment" icon={<IconSmile />} />
          </div>
          <SentimentBar items={sentiment} />
        </div>
      </div>

      <div className="chart-card">
        <div className="chart-card-header">
          <SectionHeader title="Top Topics" icon={<IconTag />} />
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={topicsChartData}
            layout="vertical"
            margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
            barSize={18}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={130} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" name="Messages" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="two-col-grid">
        <div className="chart-card">
          <div className="chart-card-header">
            <SectionHeader title="Unanswered Questions" icon={<IconAlert />} />
            <span className="badge-pill">{unanswered.length} total</span>
          </div>
          <UnansweredTable items={unanswered} />
        </div>
        <div className="chart-card">
          <div className="chart-card-header">
            <SectionHeader title="Top Locations" icon={<IconGlobe />} />
          </div>
          <LocationsTable items={locations} />
        </div>
      </div>

      <div className="analytics-footer">
        <IconClock />
        <span>All times are shown in your local timezone</span>
      </div>
      {loading && (
        <div className="analytics-loading">
          <div className="loading-spinner" />
          Loading analytics…
        </div>
      )}
    </div>
  );
}